import React, { useEffect, useState } from 'react';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import classNames from 'classnames';
import { AvailableFiltersObject, FilterCondition, UseFiltersInterface } from 'src/services/filters';
import Input from 'src/components/ui/Input/Input';
import Select, { Option } from 'src/components/ui/Select/Select';
import ReferenceFilterSelect, {
  isReferenceFilterType,
} from 'src/components/ui/ReferenceFilterSelect/ReferenceFilterSelect';
import ReferenceFilterBadge from 'src/components/ui/ReferenceFilterSelect/ReferenceFilterBadge';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import SaveSearchModal from 'src/components/modals/SaveSearchModal/SaveSearchModal';
import Badge from 'src/components/ui/Badge/Badge';
import { SingleValue } from 'react-select';
import { useEventListeners } from 'src/hooks/utils';
import {
  extractTextAndNumbers,
  formatBadgeFilterLabel,
  formatConditionDisplayValue,
  formatFilterKey,
  getOperatorTranslation,
} from 'src/utils/general';
import styles from './FilterRow.module.scss';
import Modal from '../Modal/Modal';

export interface FilterRowProps {
  primaryKeys?: { key: string; operator: string }[];
  withSaveFilters?: boolean;
  filterInstance?: UseFiltersInterface;
  singleParam?: boolean;
  multipleParams?: boolean;
  searchOnly?: boolean;
}

function isEmptyFilterValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;

  if (typeof v === 'boolean') return false;
  if (typeof v === 'number') return false;

  if (typeof v === 'string') return v.trim() === '';

  if (Array.isArray(v)) return v.length === 0;

  if (typeof v === 'object') {
    const obj = v as any;
    if ('name' in obj) {
      return typeof obj.name === 'string' && obj.name.trim() === '';
    }
    return Object.values(obj).every(val => isEmptyFilterValue(val));
  }

  return false;
}

const FilterRow: React.FC<FilterRowProps> = ({
  filterInstance,
  primaryKeys = [],
  withSaveFilters = true,
  searchOnly,
  singleParam,
  multipleParams,
}) => {
  const { t } = useTranslation();
  const {
    availableFilters = [],
    activeFilters,
    deleteFilterById,
    createFilter,
    setFilter,
    setDomain,
    filter,
    modelId,
  } = filterInstance || {};
  const [additionalFilterModalOpen, setAdditionalFilterModalOpen] = useState(false);
  const [keyPressed, setKeyPressed] = useState('');
  const [saveSearchModal, setSaveSearchModal] = useState(false);
  const [customFilter, setCustomFilter] = useState('');
  const [allFilters, setAllFilters] = React.useState<FilterCondition[]>([]);
  const prominentFilters = primaryKeys || ['id'];

  const numberOfAdditionalFilters = (availableFilters || []).length - prominentFilters.length;

  /**
   * Update filter value in allFilters based on user input
   * @param key - field name
   * @param value - value to filter
   * @param operator - operator to filter
   * @returns - void
   */
  const handleFilterValueUpdate = async (key: string, value?: string | null, operator?: string) => {
    if (!key) return;
    setAllFilters(
      allFilters.map(filter =>
        filter.field === key ? { ...filter, value: value ?? '', ...(operator ? { operator } : {}) } : filter,
      ),
    );
  };

  /**
   * Update filter operator in allFilters based on user input
   * @param key - field name
   * @param operator - operator to filter
   * @returns - void
   */
  const updateOperator = async (key: string, operator: string | null | undefined) => {
    if (!key || operator === null || operator === undefined) return;
    setAllFilters(allFilters.map(filter => (filter.field === key ? { ...filter, operator } : filter)));
  };

  /**
   * Get value for a date key
   * @param key - field name
   * @returns - value for the key
   */
  const getValueForDateKey = (key: string) => {
    const value = allFilters.find(e => e.field === key)?.value;
    return value ? dayjs(value) : null;
  };

  /**
   * Initialize available filters
   * @returns - void
   */
  const initAvailableFilters = () => {
    let filters = [] as FilterCondition[];
    if (availableFilters) {
      // Set default values for all filters
      filters = availableFilters.map((filter: any) => {
        const key = Object.keys(filter)[0];
        return { field: key, operator: '', value: '', type: filter[key].type, operators: filter[key].operators };
      });
    }

    // Set values for active filter
    if (filter && filter.id) {
      filters = filters.map(x => {
        if (filter) {
          const tmp = filter.domain.find(e => e.field === x.field);
          return tmp ? { ...x, value: tmp.value || '', operator: tmp.operator } : x;
        }
        return x;
      });
    }
    setAllFilters(filters);
  };

  /**
   * Handle submit to create a filter
   * @param name - filter name
   */
  const handleCreateFilter = async (name: string) => {
    if (name && createFilter) {
      await createFilter(allFilters, name);
      setAdditionalFilterModalOpen(false);
      setSaveSearchModal(false);
      initAvailableFilters();
    }
  };

  /**
   * Clear filter
   */
  const clearFilter = async () => {
    setAdditionalFilterModalOpen(false);
    initAvailableFilters();
    if (deleteFilterById && filter) {
      await deleteFilterById(filter.id);
    }
  };

  /**
   * Handle select of active filter
   * @param e - selected filter
   */
  const handleActiveFilterSelect = (e: SingleValue<Option>) => {
    if (!setFilter) return;
    if (e?.label.includes('Neuen Filter erstellen')) {
      setFilter(null);
      setAdditionalFilterModalOpen(true);
    } else {
      initAvailableFilters();
      if (e && e.value && activeFilters) {
        const selectedFilter = activeFilters.find(filter => filter.id.toString() === e.value);
        if (selectedFilter) {
          setFilter(selectedFilter);
        }
      } else {
        setFilter(null);
      }
    }
  };

  /**
   * Handle enter key press
   */
  const handleFilterSubmit = () => {
    // If filter is selected, update the filter
    if (filter?.id) {
      handleCreateFilter(filter.name);
    } else {
      if (!additionalFilterModalOpen) {
        setSaveSearchModal(true);
      }
      setAdditionalFilterModalOpen(false);
    }
  };

  /**
   * Handle key press to track the enter and escape key
   */
  useEventListeners([{ event: 'keydown' }], (event: Event) => {
    const e = event as KeyboardEvent;
    setKeyPressed(e.key);
  });

  useEffect(() => {
    if (keyPressed === 'Escape') {
      setAdditionalFilterModalOpen(false);
      setSaveSearchModal(false);
    }
  }, [keyPressed]);

  useEffect(() => {
    initAvailableFilters();
  }, [availableFilters, activeFilters, filter]);

  const getUniqueFieldsCount = (filters: FilterCondition[]) => {
    return new Set(filters.map(filter => filter.field)).size;
  };

  const hasDateOrStateFilter = (filters: FilterCondition[]) => {
    return filters.some(
      filter => filter.field === 'date_planned' || filter.field === 'create_date' || filter.field === 'inbound_state',
    );
  };

  const getSaleOrderFilters = (filters: FilterCondition[]) => {
    return filters.filter(item => item.field === 'partner_id' || item.field === 'name' || item.field === 'id');
  };

  const buildDomainWithOperator = (operator: string | null, filterString: string) => {
    return `[${operator ? `${operator}, ` : ''}${filterString}]`;
  };

  useEffect(() => {
    const extract = extractTextAndNumbers(customFilter);
    const { texts, numbers } = extract;

    let mappedFilters: any[] = [];

    if (texts.length > 0) {
      const allowedSearches = new Set(['name', 'partner_id', 'continent_name', 'section', 'topic', 'operators']);

      mappedFilters = allFilters
        .filter(item => item.operators?.includes('ilike') && allowedSearches.has(item.field))
        .map(filter => ({
          ...filter,
          operator: 'ilike',
          value: texts.join(' '),
        }));
    }

    if (numbers.length > 0) {
      mappedFilters = allFilters
        .filter(filter => filter.type === 'integer')
        .map(filter => ({
          ...filter,
          operator: '=',
          value: numbers.join(' '),
        }));
    }

    const allFiltersWithValue = allFilters.filter(e => !!e.value && e.field !== 'custom');
    const combinedFilters = [...allFiltersWithValue, ...mappedFilters].filter(e => !isEmptyFilterValue(e.value));

    const filterString = combinedFilters
      .map(e => {
        const fieldName = e.field === 'partner_id' ? 'partner_id.name' : e.field;

        let valueStr;
        if (e.type === 'integer' || e.type === 'float' || isReferenceFilterType(e.type)) {
          valueStr = e.value;
        } else {
          valueStr = `"${e.value}"`;
        }

        return `("${fieldName}", "${e.operator}", ${valueStr})`;
      })
      .join(', ');

    const saleOrderFilterString = getSaleOrderFilters(combinedFilters)
      .filter(e => !isEmptyFilterValue(e.value))
      .map(e => {
        const fieldName = e.field === 'partner_id' ? `${e.field}.name` : e.field;
        return `("${fieldName}", "${e.operator}", ${e.value === 'True' || e.value === 'False' ? e.value : `"${e.value}"`})`;
      })
      .join(', ');
    let domain: string;

    if (multipleParams) {
      const uniqueFields = getUniqueFieldsCount(combinedFilters);
      const operator =
        uniqueFields > 1 && customFilter
          ? '"|"'
          : uniqueFields > 1 && !hasDateOrStateFilter(combinedFilters)
            ? '"&"'
            : null;
      domain = filterString.length > 0 ? buildDomainWithOperator(operator, filterString) : '';
    } else if (searchOnly) {
      const saleOrderFilters = getSaleOrderFilters(combinedFilters);
      const uniqueSaleOrderFields = getUniqueFieldsCount(saleOrderFilters);
      domain = saleOrderFilterString
        ? buildDomainWithOperator(uniqueSaleOrderFields > 1 ? '"|"' : null, saleOrderFilterString)
        : '';
    } else {
      const uniqueFields = getUniqueFieldsCount(combinedFilters);
      let operator = null;
      if (!singleParam && uniqueFields > 1) {
        operator = '"&"';
      }
      domain = buildDomainWithOperator(operator, filterString);
    }

    if (setDomain) {
      setDomain(domain);
    }
  }, [customFilter, allFilters]);

  const renderFilterFunction = (enableOperatorSelect: boolean, renderProminentOnly?: boolean) => {
    if (!availableFilters) return null;

    return availableFilters.map((filter: AvailableFiltersObject) => {
      const key = Object.keys(filter)[0];
      if (renderProminentOnly && !prominentFilters.find(e => e.key === key)) return null;
      if (!renderProminentOnly && prominentFilters.find(e => e.key === key)) return null;

      const defaultOperator = enableOperatorSelect ? undefined : primaryKeys.find(e => e.key === key)?.operator;

      const d = filter[key];

      if (!d || !d.operators) return null;

      const operatorOptions = d.operators.map((e: string) => ({ value: e, label: getOperatorTranslation(e) }));
      const defaultValue = operatorOptions.find(
        (e: Option) => e.value === allFilters.find(e => e.field === key)?.operator,
      );
      const { type, options = [] } = d;
      return (
        <div
          key={key}
          className={classNames(
            { 'flex flex-1 flex-shrink-0': !enableOperatorSelect },
            {
              [styles.filterLine]: enableOperatorSelect,
            },
          )}>
          {enableOperatorSelect && (
            <Select
              onChange={e => updateOperator(key, e?.value)}
              options={operatorOptions}
              label={t(`general.${key}`)}
              defaultValue={defaultValue}
              isClearable
              menuFixed
            />
          )}
          {type === 'datetime' && (
            <DatePicker
              key={getValueForDateKey(key)?.format('DD.MM.YYYY')}
              label={t(`general.${key}`)}
              disabled={enableOperatorSelect && !allFilters.find(e => e.field === key)?.operator}
              defaultValue={getValueForDateKey(key)}
              onChange={e => handleFilterValueUpdate(key, e, defaultOperator)}
            />
          )}

          {(type === 'char' || type === 'integer' || type === 'float') && (
            <Input
              disabled={enableOperatorSelect && !allFilters.find(e => e.field === key)?.operator}
              label={renderProminentOnly ? t(`general.${key}`) : ''}
              placeholder={t('general.input_placeholder')}
              value={allFilters.find(e => e.field === key)?.value}
              onChange={e => handleFilterValueUpdate(key, e.target.value, defaultOperator)}
              clearable
              type={(type === 'integer' || type === 'float') && key !== 'id' ? 'number' : 'text'}
            />
          )}
          {type === 'selection' && (
            <Select
              onChange={e => handleFilterValueUpdate(key, e?.value, defaultOperator)}
              options={options.map(e => ({ value: e, label: formatFilterKey(e, modelId) }))}
              defaultValue={options
                .map(e => ({ value: e, label: formatFilterKey(e, modelId) }))
                .find(e => e.value === allFilters.find(e => e.field === key)?.value)}
              key={defaultValue?.value}
              isClearable
              disabled={enableOperatorSelect && !allFilters.find(e => e.field === key)?.operator}
              menuFixed
            />
          )}
          {type === 'boolean' && (
            <Select
              isClearable
              disabled={enableOperatorSelect && !allFilters.find(e => e.field === key)?.operator}
              onChange={e => handleFilterValueUpdate(key, e?.value)}
              menuFixed
              options={[
                {
                  value: 'True',
                  label: t('general.true'),
                },
                {
                  value: 'False',
                  label: t('general.false'),
                },
              ]}
            />
          )}
          {isReferenceFilterType(type) && (
            <div className="flex-1">
              <ReferenceFilterSelect
                type={type}
                disabled={enableOperatorSelect && !allFilters.find(e => e.field === key)?.operator}
                value={allFilters.find(e => e.field === key)?.value}
                onChange={value => handleFilterValueUpdate(key, value, defaultOperator)}
              />
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      {withSaveFilters && (
        <div className="flex items-end mb-16">
          {activeFilters && (
            <div className={styles.activeFilters}>
              <Select
                key={`${filter?.id ?? 'none'}-${activeFilters?.length ?? 0}`}
                placeholder={t('general.select_filter')}
                label={t('general.my_filters')}
                isClearable
                defaultValue={activeFilters
                  .map(e => ({ value: e.id.toString(), label: e.name }))
                  .find(e => e.value === filter?.id.toString())}
                options={[
                  { value: '', label: t('general.create_new_filter') },
                  ...activeFilters.map(e => ({ value: e.id.toString(), label: e.name })),
                ]}
                onChange={handleActiveFilterSelect}
              />
            </div>
          )}
          <div className="ml-auto flex gap-16">
            <Button
              onClick={handleFilterSubmit}
              disabled={!allFilters.some(e => !!e.value)}
              tone={filter?.id ? ButtonToneProps.PRIMARY_BLUE : ButtonToneProps.SECONDARY}
              title={filter?.id ? t('general.adapt_filter') : t('general.save_search')}
            />
            {filter && filter?.id && (
              <Button
                onClick={() => deleteFilterById && deleteFilterById(filter.id)}
                tone={ButtonToneProps.SECONDARY}
                title={t('general.delete_filter')}
              />
            )}
          </div>
        </div>
      )}
      <div className={classNames(styles.root, 'flex flex-wrap gap-16 w-full pt-12 pb-12 px-12')}>
        {prominentFilters.find(e => e.key === 'custom') && (
          <div className={classNames('flex-1 flex-shrink-0')}>
            <Input
              label={t('general.search_label')}
              placeholder={t('general.search_placeholder')}
              value={customFilter}
              onChange={e => setCustomFilter(e.target.value)}
              clearable
              icon="search"
              className="opacity-70"
            />
          </div>
        )}
        {renderFilterFunction(false, true)}
        {numberOfAdditionalFilters > 0 && !searchOnly && (
          <div className="mt-auto flex-1">
            <Button
              className="flex-shrink-0"
              onClick={() => setAdditionalFilterModalOpen(true)}
              title={t('general.additional_filters')}
              tone={ButtonToneProps.SECONDARY}
              icon="tune"
              numberIndicator={numberOfAdditionalFilters}
            />
          </div>
        )}
      </div>
      {allFilters && allFilters.filter(e => !!e.value).length > 0 && (
        <div className="flex flex-wrap gap-16 items-center mt-16">
          {allFilters
            .filter(e => !!e.value)
            .map(f => {
              if (isReferenceFilterType(f.type)) {
                return (
                  <ReferenceFilterBadge
                    key={f.field}
                    className="capitalize"
                    type={f.type as string}
                    value={String(f.value)}
                    preLabel={`${t(`general.${f.field}`)}:`}
                    onRemove={() => handleFilterValueUpdate(f.field, '', '')}
                  />
                );
              }
              return (
                <Badge
                  key={f.field}
                  className="capitalize"
                  onClick={() => handleFilterValueUpdate(f.field, '', '')}
                  preLabel={`${t(`general.${f.field}`)}:`}
                  label={(() => {
                    if (f.type === 'datetime') {
                      return dayjs(f.value).format('DD.MM.YYYY');
                    }
                    if (f.type === 'boolean') {
                      return f.value ? t('general.true') : t('general.false');
                    }
                    if (f.type === 'selection') {
                      return formatConditionDisplayValue(f.value);
                    }
                    return formatBadgeFilterLabel(f.value);
                  })()}
                  expanded
                  icon="deleteCircle"
                />
              );
            })}
        </div>
      )}
      {saveSearchModal && (
        <SaveSearchModal
          onSubmit={name => handleCreateFilter(name)}
          isOpen={saveSearchModal}
          onClose={() => setSaveSearchModal(false)}
        />
      )}
      {additionalFilterModalOpen && (
        <Modal
          hasBackdrop
          hasCloseButton
          isOpen={additionalFilterModalOpen}
          onClose={() => setAdditionalFilterModalOpen(false)}>
          <div className={styles.modal}>
            <div className={styles.head}>{t('general.additional_filters')}</div>
            <div className={classNames(styles.content, 'flex flex-col gap-24')}>{renderFilterFunction(true)}</div>
            <div className={styles.actions}>
              <Button
                onClick={clearFilter}
                tone={ButtonToneProps.SECONDARY}
                disabled={!allFilters.filter(e => !!e.value).length}
                title={t('general.delete_filter')}
              />
              <Button onClick={handleFilterSubmit} tone={ButtonToneProps.PRIMARY_BLUE} title={t('general.apply')} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default FilterRow;
